// Small pieces shared by 3.2 and 3.3: the status badge, the one-way status strip, the read-only banner.
import { Badge, type BadgeTone } from '@/kit/ui'
import { STATUS_LABEL, STRIP, batchName } from '@/lib/batch'
import type { Batch, BatchStatus } from '@/types'

const TONE: Record<BatchStatus, BadgeTone> = { draft: 'warning', tests_passed: 'info', built: 'primary', live: 'success', previous: 'gray', retired: 'gray' }

export function BatchStatusBadge({ status }: { status: BatchStatus }) {
  return <Badge tone={TONE[status]}>{STATUS_LABEL[status]}</Badge>
}

/** draft → tests passed → built → live, the current step highlighted, the "Next:" text at the end. */
export function StatusStrip({ batch, next }: { batch: Batch; next: string }) {
  const idx = STRIP.indexOf(batch.status)
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-2xs dark:border-slate-700 dark:bg-surface-dark" data-testid="status-strip">
      {STRIP.map((s, i) => (
        <span key={s} className="flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 ${i === idx ? 'bg-primary text-white' : i < idx ? 'bg-primary-soft text-primary-text' : 'bg-slate-100 text-muted dark:bg-slate-800'}`}>{STATUS_LABEL[s]}</span>
          {i < STRIP.length - 1 && <span className="text-muted" aria-hidden="true">›</span>}
        </span>
      ))}
      {idx < 0 && <BatchStatusBadge status={batch.status} />}
      <span className="ml-auto text-muted">{next}</span>
    </div>
  )
}

export function ReadOnlyBanner({ batch }: { batch: Batch }) {
  return (
    <div className="mb-3 rounded border-l-[3px] border-primary bg-primary-soft/40 px-3 py-2 text-xs dark:bg-primary-soft/20" role="note" data-testid="read-only-banner">
      <b>{batchName(batch)}</b> is <b>{STATUS_LABEL[batch.status]}</b> and read-only (G6). To change the code: <b>New version from this</b>.
      {batch.bundle && <> Bundle <code>{batch.bundle.key}</code> · <code>{batch.bundle.checksum.slice(0, 19)}…</code>.</>}
    </div>
  )
}
